import {
  Column,
  Query,
  type AnyColumn,
  type ColumnMap,
  type ColumnMapType,
  type DatabaseDriver,
  type ExecuteOpts,
  type SqlFragment,
  type Table,
  type TransactionHandle,
} from '@w/db'
import { z as s, type ZodType as Schema } from '../../db/src/core/schema'
import type { Tel } from '@w/tel'
import type { MutationOptions, QueryOptions, QueryOptionsWithQueryFn } from './types'

export type SubscribingDriver = DatabaseDriver & {
  subscribe(sql: string, onInvalidate: () => void): Promise<() => void>
}

type QueryOptionsWithTel<TData> = Omit<QueryOptionsWithQueryFn<TData>, 'queryFn'> & {
  queryFn: (qctx?: { tel?: Tel }) => Promise<TData>
}
type QueryOverrides = Partial<Omit<QueryOptions, 'queryKey' | 'queryFn'>>
type MutationOverrides<TData = unknown, TVariables = unknown> = Partial<
  Omit<MutationOptions<TData, TVariables>, 'mutationFn'>
>

export function queryOptions<M extends { [K in keyof M]: AnyColumn }>(
  query: Query,
  columns: M,
  overrides?: QueryOverrides,
): QueryOptionsWithTel<ColumnMapType<M>[]>
export function queryOptions<T>(
  query: Query,
  schema: Schema<T>,
  overrides?: QueryOverrides,
): QueryOptionsWithTel<T[]>
export function queryOptions<T>(
  query: Query,
  opts: ExecuteOpts<T>,
  overrides?: QueryOverrides,
): QueryOptionsWithTel<T[]>
export function queryOptions<T>(
  query: Query,
  schemaOrOpts: Schema<T> | ExecuteOpts<T> | ColumnMap,
  overrides?: QueryOverrides,
): QueryOptionsWithTel<T[]> {
  if (!query.driver) {
    throw new Error(
      'Cannot build query options from SQL fragment. Use db.query`...` to create an executable query.',
    )
  }
  const driver: DatabaseDriver & Partial<SubscribingDriver> = query.driver
  const subscribe = driver.subscribe?.bind(driver)
  const { sql, params } = query.toSql()

  return {
    ...overrides,
    ...(subscribe ? { subscribe: (onInvalidate: () => void) => subscribe(sql, onInvalidate) } : {}),
    queryKey: [sql, ...params],
    queryFn: (qctx) => {
      if (isColumnMap(schemaOrOpts)) {
        const shape: Record<string, Schema> = {}
        for (const [key, column] of Object.entries(schemaOrOpts)) {
          shape[key] = column.zodType
        }
        return query.execute({ schema: s.object(shape), tel: qctx?.tel }) as Promise<T[]>
      }
      if ('schema' in schemaOrOpts) {
        return query.execute({ ...schemaOrOpts, tel: qctx?.tel ?? schemaOrOpts.tel })
      }
      return query.execute({ schema: schemaOrOpts, tel: qctx?.tel })
    },
  }
}

export function mutationOptions<TData = void, TVariables = void>(
  queryOrTransaction: Query | TransactionHandle<unknown>,
  overrides?: MutationOverrides<TData, TVariables>,
): MutationOptions<TData, TVariables> {
  if (!(queryOrTransaction instanceof Query) && queryOrTransaction.started) {
    throw new Error(
      'mutationOptions() must be chained synchronously before the transaction runs: db.transaction(fn).mutationOptions()',
    )
  }
  if (queryOrTransaction instanceof Query && !queryOrTransaction.driver) {
    throw new Error(
      'Cannot build mutation options from SQL fragment. Use db.query`...` to create an executable query.',
    )
  }
  return {
    ...overrides,
    mutationFn: async (_variables) => {
      if (queryOrTransaction instanceof Query) {
        await queryOrTransaction.run()
      } else {
        await queryOrTransaction
      }
      return undefined as TData
    },
  }
}

export function insertOptions<Columns extends Record<string, AnyColumn>>(
  table: Table<Columns>,
  overrides?: MutationOverrides,
): MutationOptions {
  const db = table.__queryContext
  return {
    ...overrides,
    mutationFn: (data: unknown) =>
      db.query`${table.insert(data as Table<Columns>['__insertType'])}`.run(),
  }
}

export function insertManyOptions<Columns extends Record<string, AnyColumn>>(
  table: Table<Columns>,
  overrides?: MutationOverrides,
): MutationOptions {
  const db = table.__queryContext
  return {
    ...overrides,
    mutationFn: (data: unknown) =>
      db.query`${table.insert(data as Table<Columns>['__insertType'][])}`.run(),
  }
}

export function updateOptions<Columns extends Record<string, AnyColumn>>(
  table: Table<Columns>,
  overrides?: MutationOverrides,
): MutationOptions {
  const db = table.__queryContext
  return {
    ...overrides,
    mutationFn: (options: unknown) => {
      const parsed = options as {
        data: Partial<Table<Columns>['__insertType']>
        where: SqlFragment
      }
      return db.query`${table.update(parsed.data)} WHERE ${parsed.where}`.run()
    },
  }
}

export function deleteOptions<Columns extends Record<string, AnyColumn>>(
  table: Table<Columns>,
  overrides?: MutationOverrides,
): MutationOptions {
  const db = table.__queryContext
  return {
    ...overrides,
    mutationFn: (options: unknown) => {
      const parsed = options as { where: SqlFragment }
      return db.query`DELETE FROM ${table} WHERE ${parsed.where}`.run()
    },
  }
}

function isColumnMap(value: unknown): value is ColumnMap {
  if (value === null || typeof value !== 'object' || 'schema' in value) return false
  const columns = Object.values(value)
  return columns.length > 0 && columns.every(column => column instanceof Column)
}
